"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

interface IframeHeightSyncProps {
  /**
   * Target origin for postMessage. Use '*' for testing, but specify the parent domain in production.
   * Example: 'https://www.mollerbil.no'
   */
  targetOrigin?: string;
}

//NOTE: This component is for testing only at the moment, and is not production ready.

export function IframeHeightSync({
  targetOrigin = "*",
}: IframeHeightSyncProps) {
  const pathname = usePathname();

  useEffect(() => {
    // Only run if we're in an iframe
    if (window.self === window.top) {
      return;
    }

    // Remove height: 100% from html and body when in iframe
    document.documentElement.style.height = "auto";
    document.body.style.height = "auto";

    // Disable scrolling in iframe
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    function sendHeight() {
      // Only use body measurements, not documentElement (html element has flex layout issues)
      const height = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
      );
      window.parent.postMessage(
        { type: "setHeight", height: height },
        targetOrigin,
      );
    }

    // Send height immediately
    sendHeight();

    // Poll for height changes in case content loads async
    let lastHeight = 0;
    const pollInterval = setInterval(() => {
      const currentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
      );
      if (currentHeight !== lastHeight && currentHeight > 0) {
        lastHeight = currentHeight;
        sendHeight();
      }
    }, 100);

    // Stop polling after 10 seconds
    const stopPolling = setTimeout(() => clearInterval(pollInterval), 10000);

    // Send height on load (if not already loaded)
    const needsLoadListener = document.readyState !== "complete";
    if (document.readyState === "complete") {
      setTimeout(sendHeight, 10);
    } else {
      window.addEventListener("load", sendHeight);
    }

    // Send height when window resizes
    window.addEventListener("resize", sendHeight);

    // Send height when content changes
    const observer = new ResizeObserver(() => {
      sendHeight();
    });
    observer.observe(document.body);
    observer.observe(document.documentElement);

    // Also observe all direct children of body to catch expansions/collapses
    const observeChildren = () => {
      document.body.childNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          observer.observe(node as Element);
        }
      });
    };
    observeChildren();

    // Send height on any click (catches "vis flere" button clicks and collapses)
    const handleClick = () => {
      setTimeout(sendHeight, 10);
      setTimeout(sendHeight, 100);
      setTimeout(sendHeight, 300);
      setTimeout(sendHeight, 500);
    };
    document.addEventListener("click", handleClick);

    // Cleanup
    return () => {
      clearInterval(pollInterval);
      clearTimeout(stopPolling);
      if (needsLoadListener) {
        window.removeEventListener("load", sendHeight);
      }
      window.removeEventListener("resize", sendHeight);
      document.removeEventListener("click", handleClick);
      observer.disconnect();
    };
  }, [targetOrigin, pathname]);

  return null;
}
